/**
 * shadcn-style Button primitive — kept available for non-themed UI
 * (admin tables, secondary controls). For player-facing CTAs use
 * <CasinoButton> instead.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'outline' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  default:
    'bg-cabinet-gold text-cabinet-purple-ink hover:brightness-105 active:translate-y-px',
  outline:
    'border border-cabinet-cream/30 text-cabinet-cream hover:border-cabinet-gold hover:text-cabinet-gold',
  ghost: 'text-cabinet-cream/80 hover:text-cabinet-cream hover:bg-cabinet-cream/5',
  destructive:
    'bg-cabinet-flame-red text-cabinet-cream hover:brightness-110',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cabinet-gold disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';
